import { useMemo, useState } from "react";
import { Download, Search, X } from "lucide-react";
import { Avatar, OwnerChip, PriorityBadge, ProductTag, StatusBadge } from "./widgets";
import { formatCurrency, formatDateTime, productLabel, relativeTime } from "./format";
import {
  PRIORITY_META,
  PRODUCT_ORDER,
  PRODUCT_META,
  STATUS_META,
  STATUS_ORDER,
  type AdminLead,
  type LeadStatus
} from "./types";

type SortKey = "newest" | "oldest" | "name";

function csvCell(value: unknown) {
  let text = String(value ?? "").replace(/\r?\n/g, " ");
  // Ochrona przed CSV injection: pola zaczynające się od = + - @ (tab/CR) mogą być
  // potraktowane jak formuła w Excelu/Sheets. Dane leadów pochodzą z publicznego formularza.
  if (/^[=+\-@\t\r]/.test(text)) {
    text = `'${text}`;
  }
  return `"${text.replace(/"/g, '""')}"`;
}

export function LeadsTable({
  leads,
  query,
  setQuery,
  onOpen
}: {
  leads: AdminLead[];
  query: string;
  setQuery: (value: string) => void;
  onOpen: (id: string) => void;
}) {
  const [status, setStatus] = useState<"all" | LeadStatus>("all");
  const [product, setProduct] = useState<"all" | string>("all");
  const [owner, setOwner] = useState<"all" | "__none__" | string>("all");
  const [sort, setSort] = useState<SortKey>("newest");

  const owners = useMemo(() => {
    const set = new Set<string>();
    leads.forEach((lead) => {
      if (lead.owner) set.add(lead.owner);
    });
    return [...set].sort((a, b) => a.localeCompare(b, "pl"));
  }, [leads]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const rows = leads.filter((lead) => {
      if (status !== "all" && lead.status !== status) return false;
      if (product !== "all" && lead.product !== product) return false;
      if (owner === "__none__" && lead.owner) return false;
      if (owner !== "all" && owner !== "__none__" && lead.owner !== owner) return false;
      if (q) {
        const hay = `${lead.name} ${lead.phone} ${lead.email} ${lead.city} ${productLabel(lead.product)}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    rows.sort((a, b) => {
      if (sort === "name") return a.name.localeCompare(b.name, "pl");
      if (sort === "oldest") return a.createdAt.localeCompare(b.createdAt);
      return b.createdAt.localeCompare(a.createdAt);
    });
    return rows;
  }, [leads, query, status, product, owner, sort]);

  const filtersActive = status !== "all" || product !== "all" || owner !== "all" || query.trim() !== "";

  const resetFilters = () => {
    setStatus("all");
    setProduct("all");
    setOwner("all");
    setQuery("");
  };

  const exportCsv = () => {
    const headers = [
      "Data",
      "Imię i nazwisko",
      "Telefon",
      "E-mail",
      "Kod pocztowy",
      "Produkt",
      "Status",
      "Priorytet",
      "Opiekun",
      "Pora kontaktu",
      "Budżet (mies.)",
      "Źródło",
      "Wiadomość",
      "Zgoda kontakt",
      "Zgoda RODO",
      "Zgoda marketing"
    ];
    const rows = filtered.map((lead) => [
      formatDateTime(lead.createdAt),
      lead.name,
      lead.phone,
      lead.email,
      lead.city,
      productLabel(lead.product),
      STATUS_META[lead.status].label,
      PRIORITY_META[lead.priority].label,
      lead.owner,
      lead.contactTime,
      lead.budget != null ? String(lead.budget) : "",
      lead.source,
      lead.message,
      lead.contactConsent ? "tak" : "nie",
      lead.privacyConsent ? "tak" : "nie",
      lead.marketingConsent ? "tak" : "nie"
    ]);
    const csv = [headers, ...rows].map((row) => row.map(csvCell).join(";")).join("\n");
    const blob = new Blob([`﻿${csv}`], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `ochro-leady-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="crm-view">
      <div className="crm-toolbar">
        <div className="crm-search">
          <Search size={17} aria-hidden="true" />
          <input
            value={query}
            placeholder="Szukaj: nazwisko, telefon, e-mail, kod pocztowy…"
            onChange={(event) => setQuery(event.target.value)}
          />
          {query && (
            <button type="button" className="crm-search-clear" onClick={() => setQuery("")} aria-label="Wyczyść">
              <X size={15} />
            </button>
          )}
        </div>

        <div className="crm-filters">
          <select className="crm-select" value={status} onChange={(e) => setStatus(e.target.value as typeof status)}>
            <option value="all">Wszystkie etapy</option>
            {STATUS_ORDER.map((s) => (
              <option key={s} value={s}>
                {STATUS_META[s].label}
              </option>
            ))}
          </select>

          <select className="crm-select" value={product} onChange={(e) => setProduct(e.target.value)}>
            <option value="all">Wszystkie produkty</option>
            {PRODUCT_ORDER.map((p) => (
              <option key={p} value={p}>
                {PRODUCT_META[p].short}
              </option>
            ))}
          </select>

          <select className="crm-select" value={owner} onChange={(e) => setOwner(e.target.value)}>
            <option value="all">Wszyscy opiekunowie</option>
            <option value="__none__">Nieprzypisane</option>
            {owners.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>

          <select className="crm-select" value={sort} onChange={(e) => setSort(e.target.value as SortKey)}>
            <option value="newest">Najnowsze</option>
            <option value="oldest">Najstarsze</option>
            <option value="name">Nazwisko A–Z</option>
          </select>
        </div>
      </div>

      <div className="crm-table-meta">
        <span>
          <strong>{filtered.length}</strong> z {leads.length} zgłoszeń
          {filtersActive && (
            <button type="button" className="crm-link" onClick={resetFilters}>
              Wyczyść filtry
            </button>
          )}
        </span>
        <button type="button" className="crm-btn crm-btn--ghost" onClick={exportCsv} disabled={filtered.length === 0}>
          <Download size={16} aria-hidden="true" />
          Eksport CSV
        </button>
      </div>

      <div className="crm-table-wrap">
        <table className="crm-table">
          <thead>
            <tr>
              <th>Osoba</th>
              <th>Kontakt</th>
              <th>Produkt</th>
              <th>Etap</th>
              <th>Opiekun</th>
              <th>Priorytet</th>
              <th>Utworzono</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={7} className="crm-table-empty">
                  Brak zgłoszeń spełniających kryteria.
                </td>
              </tr>
            ) : (
              filtered.map((lead) => (
                <tr key={lead.id} onClick={() => onOpen(lead.id)} tabIndex={0}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") onOpen(lead.id);
                  }}
                >
                  <td>
                    <div className="crm-cell-person">
                      <Avatar name={lead.name} size={36} />
                      <div>
                        <strong>{lead.name}</strong>
                        <span>{lead.city || "—"}</span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="crm-cell-contact">
                      <a href={`tel:${lead.phone}`} onClick={(e) => e.stopPropagation()}>
                        {lead.phone}
                      </a>
                      <span>{lead.email || "—"}</span>
                    </div>
                  </td>
                  <td>
                    <ProductTag product={lead.product} />
                  </td>
                  <td>
                    <StatusBadge status={lead.status} />
                  </td>
                  <td>
                    <OwnerChip owner={lead.owner} />
                  </td>
                  <td>
                    <PriorityBadge priority={lead.priority} />
                  </td>
                  <td className="crm-cell-time">{relativeTime(lead.createdAt)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
